import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { removeFromCartAPI } from "../api/cartApiService";
import { useCart } from "./useCart";
import { useWishlist } from "./useWishlist";
import { setCartCount } from "../store/apiCountsSlice";

interface UseProductCartCardOptions {
  product: any;
  onRemoved?: () => void;
}

export interface UseProductCartCardReturn {
  isInWishlist: boolean;
  isWishlistLoading: boolean;
  isBlurLoading: boolean;
  mappingCode: string;
  handleWishlistToggle: () => Promise<void>;
  handleRemove: () => Promise<void>;
}

export const useProductCartCard = ({
  product,
  onRemoved,
}: UseProductCartCardOptions): UseProductCartCardReturn => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state: any) => state.auth.userInfo);

  const { removeFromCart } = useCart();
  const { addToWishlist, removeFromWishlist, isWishlisted } = useWishlist();

  const [isBlurLoading, setIsBlurLoading] = useState(false);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);

  const mappingCode: string =
    product?.sellerPriceDTO?.mappingCode ||
    product?.mappingCode ||
    String(product?.variationCode ?? "");

  const getInitialFavourite = (): boolean => {
    if (typeof product?.sellerPriceDTO?.isFavourite === "boolean") {
      return product.sellerPriceDTO.isFavourite;
    }
    if (typeof product?.isFavourite === "boolean") {
      return product.isFavourite;
    }
    return false;
  };

  const [isInWishlist, setIsInWishlist] = useState<boolean>(getInitialFavourite);

  // Sync with API-provided favourite flag when product data changes
  useEffect(() => {
    if (typeof product?.sellerPriceDTO?.isFavourite === "boolean") {
      setIsInWishlist(product.sellerPriceDTO.isFavourite);
    } else if (typeof product?.isFavourite === "boolean") {
      setIsInWishlist(product.isFavourite);
    }
  }, [product?.sellerPriceDTO?.isFavourite, product?.isFavourite, product?.variationCode]);

  // Fall back to Redux store state when API flag is absent
  const storeWishlisted = isWishlisted(mappingCode);
  useEffect(() => {
    if (
      typeof product?.sellerPriceDTO?.isFavourite !== "boolean" &&
      typeof product?.isFavourite !== "boolean"
    ) {
      setIsInWishlist(storeWishlisted);
    }
  }, [storeWishlisted, product?.sellerPriceDTO?.isFavourite, product?.isFavourite]);

  const handleRemove = async (): Promise<void> => {
    const result = await Swal.fire({
      title: "Remove from Cart?",
      text: "Do you want to remove this item from your cart?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove",
    });

    if (!result.isConfirmed) return;

    setIsBlurLoading(true);
    const apiResult = await removeFromCartAPI(mappingCode);

    if (apiResult.success) {
      removeFromCart(mappingCode);

      if ((userInfo?.user_id || userInfo?.guest_id) && apiResult.count !== undefined) {
        dispatch(setCartCount(apiResult.count));
      }

      setIsBlurLoading(false);
      await Swal.fire({
        icon: "success",
        title: "Removed from Cart",
        timer: 1200,
        showConfirmButton: false,
      });

      onRemoved?.();
    } else {
      setIsBlurLoading(false);
    }
  };

  const handleWishlistToggle = async (): Promise<void> => {
    if (isWishlistLoading) return;

    if (isInWishlist) {
      const r = await Swal.fire({
        title: "Remove from Wishlist?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
      });
      if (!r.isConfirmed) return;

      setIsWishlistLoading(true);
      setIsInWishlist(false);
      await removeFromWishlist(mappingCode);
      setIsWishlistLoading(false);
    } else {
      const r = await Swal.fire({
        title: "Move to Wishlist?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
      });
      if (!r.isConfirmed) return;

      setIsWishlistLoading(true);
      await addToWishlist(mappingCode);
      setIsInWishlist(true);
      await removeFromCart(mappingCode);
      setIsWishlistLoading(false);
    }

    onRemoved?.();
  };

  return {
    isInWishlist,
    isWishlistLoading,
    isBlurLoading,
    mappingCode,
    handleWishlistToggle,
    handleRemove,
  };
};
